import { Box, Button, Stack, Typography, keyframes } from "@mui/material";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import React from "react";
import { useNavigate } from "react-router-dom";

interface PageNotFoundProps {
  pageNotFound: boolean;
}

const floatAnim = keyframes`
  0%   { transform: translateY(0px); }
  50%  { transform: translateY(-12px); }
  100% { transform: translateY(0px); }
`;

const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

export const PageNotFound: React.FunctionComponent<PageNotFoundProps> = (
  props,
) => {
  const navigate = useNavigate();
  const title = props.pageNotFound ? "404" : "Oops!";
  const heading = props.pageNotFound ? "Page not found" : "Invalid URL";
  const message = props.pageNotFound
    ? "The page you’re looking for doesn’t exist, was moved, or you may not have access to it."
    : "Please enter a valid URL to access the store.";

  return (
    <Box
      sx={{
        minHeight: "85vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 6,
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(135deg, #eef2ff 0%, #f5f3ff 50%, #fdf2f8 100%)",
      }}
    >
      {/* Decorative blurred blobs */}
      <Box
        sx={{
          position: "absolute",
          top: "-120px",
          left: "-120px",
          width: 360,
          height: 360,
          borderRadius: "50%",
          background:
            "radial-gradient(circle at center, rgba(99,102,241,0.45), transparent 70%)",
          filter: "blur(20px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-140px",
          right: "-140px",
          width: 420,
          height: 420,
          borderRadius: "50%",
          background:
            "radial-gradient(circle at center, rgba(236,72,153,0.40), transparent 70%)",
          filter: "blur(20px)",
        }}
      />

      <Stack
        spacing={3}
        alignItems="center"
        sx={{
          position: "relative",
          zIndex: 1,
          maxWidth: 560,
          textAlign: "center",
          p: { xs: 3, sm: 5 },
          borderRadius: 4,
          backdropFilter: "blur(14px)",
          backgroundColor: "rgba(255,255,255,0.65)",
          border: "1px solid rgba(255,255,255,0.6)",
          boxShadow: "0 20px 60px rgba(31, 41, 55, 0.12)",
        }}
      >
        <Box
          sx={{
            width: 96,
            height: 96,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background:
              "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)",
            color: "#fff",
            animation: `${floatAnim} 4s ease-in-out infinite`,
            boxShadow: "0 12px 30px rgba(139, 92, 246, 0.35)",
          }}
        >
          <SearchOffRoundedIcon sx={{ fontSize: 52 }} />
        </Box>

        <Typography
          component="div"
          sx={{
            fontWeight: 800,
            fontSize: { xs: "5rem", sm: "7rem" },
            lineHeight: 1,
            letterSpacing: "-0.04em",
            backgroundImage:
              "linear-gradient(90deg, #6366f1, #8b5cf6, #ec4899, #6366f1)",
            backgroundSize: "200% auto",
            color: "transparent",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            animation: `${gradientShift} 6s ease infinite`,
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: "text.primary" }}
        >
          {heading}
        </Typography>

        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {message}
        </Typography>

        {props.pageNotFound && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ pt: 1 }}
          >
            <Button
              variant="outlined"
              size="large"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => navigate(-1)}
              sx={{ borderRadius: 99, px: 3, textTransform: "none" }}
            >
              Go back
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<HomeRoundedIcon />}
              onClick={() => navigate("/")}
              sx={{
                borderRadius: 99,
                px: 3,
                textTransform: "none",
                background:
                  "linear-gradient(90deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)",
                boxShadow: "0 10px 24px rgba(139, 92, 246, 0.35)",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #4f46e5 0%, #7c3aed 50%, #db2777 100%)",
                },
              }}
            >
              Back to Dashboard
            </Button>
          </Stack>
        )}
      </Stack>
    </Box>
  );
};
